import { FormEvent, ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Field,
  Fieldset,
  Heading,
  Input,
  Label,
  Paragraph,
  Tag,
  ValidationMessage,
} from '@digdir/designsystemet-react';
import type { ProposalError, ProposalRequest, TeamMeasures } from '../../../api/workEnvironmentTypes';
import { useProposeMeasureMutation } from '../../../hooks/mutations/useTeamMeasuresMutations';
import { FieldError, validationErrors } from '../../WorkEnvironmentSurveyDetail/assessmentView';
import { FormErrorSummary, TextField } from '../../WorkEnvironmentSurveyDetail/components/FormParts';
import {
  areasByNeed,
  charactersLeft,
  MAX_TITLE_LENGTH,
  PROPOSAL_FIELDS,
  proposalFieldErrors,
} from '../../WorkEnvironmentSurveyDetail/measuresView';
import { areaName, LEVEL_COLORS } from '../../WorkEnvironmentSurveyDetail/riskView';
import form from '../../FormSubmission/WorkEnvironmentFormSubmission.module.css';
import classes from '../WorkEnvironmentTeamMeasures.module.css';

type Props = { uuid: string; measures: TeamMeasures };

const EMPTY = { factorKey: '', title: '', reason: '' };

/** Anonymous. A person can send as many proposals as they like before the deadline. */
export const ProposeForm = ({ uuid, measures }: Props): ReactElement => {
  const { t } = useTranslation();
  const [proposal, setProposal] = useState<ProposalRequest>(EMPTY);
  const [errors, setErrors] = useState<FieldError[]>([]);
  const [sent, setSent] = useState(false);
  const propose = useProposeMeasureMutation(uuid);
  const errorFor = (fieldId: string) => errors.find((error) => error.fieldId === fieldId)?.message;
  const { needing, others } = areasByNeed(measures.areas);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setSent(false);
    propose.mutate(proposal, {
      onSuccess: () => {
        setErrors([]);
        setProposal(EMPTY);
        setSent(true);
      },
      onError: (error) => {
        const invalid = validationErrors<ProposalError>(error);
        if (invalid) setErrors(proposalFieldErrors(invalid, t));
      },
    });
  };

  const areaGroup = (title: string, areas: TeamMeasures['areas']) =>
    areas.length > 0 && (
      <div className={classes.group}>
        <Heading level={3} data-size="2xs">
          {title}
        </Heading>
        <ul className={classes.choices}>
          {areas.map((area) => (
            <li key={area.key}>
              <label className={classes.choice}>
                <input
                  type="radio"
                  name="proposal-area"
                  id={area.key === (needing[0] ?? others[0])?.key ? PROPOSAL_FIELDS.area : undefined}
                  className="ds-input"
                  value={area.key}
                  checked={proposal.factorKey === area.key}
                  onChange={() => setProposal({ ...proposal, factorKey: area.key })}
                />
                <span className={classes.choiceText}>
                  <span className="ds-label">{areaName(area, t)}</span>
                  <span className={classes.choiceMeta}>
                    <Tag data-size="sm" data-color={LEVEL_COLORS[area.level]}>
                      {t(`workEnvironmentRisk.matrix.levels.${area.level}`)}
                    </Tag>
                    <span className={`ds-paragraph ${form.subtle}`} data-size="xs">
                      {area.proposals > 0
                        ? t('workEnvironmentMeasures.areas.proposals', { count: area.proposals })
                        : t('workEnvironmentMeasures.areas.none')}
                    </span>
                  </span>
                </span>
              </label>
            </li>
          ))}
        </ul>
      </div>
    );

  return (
    <form onSubmit={submit} noValidate className={form.form}>
      <Alert data-color="warning" className={classes.warning}>
        <Heading level={2} data-size="xs">
          {t('workEnvironmentMeasures.team.warningTitle')}
        </Heading>
        <Paragraph data-size="sm">{t('workEnvironmentMeasures.team.warningText')}</Paragraph>
        <Paragraph data-size="sm">{t('workEnvironmentMeasures.team.goodExample')}</Paragraph>
        <Paragraph data-size="sm">{t('workEnvironmentMeasures.team.badExample')}</Paragraph>
      </Alert>
      {sent && (
        <Alert data-color="success" role="status">
          <Paragraph>{t('workEnvironmentMeasures.team.sent')}</Paragraph>
        </Alert>
      )}
      <FormErrorSummary errors={errors} />
      <Fieldset>
        <Fieldset.Legend>{t('workEnvironmentMeasures.team.areaQuestion')}</Fieldset.Legend>
        {areaGroup(t('workEnvironmentMeasures.team.needing'), needing)}
        {areaGroup(t('workEnvironmentMeasures.team.others'), others)}
        {errorFor(PROPOSAL_FIELDS.area) && <ValidationMessage>{errorFor(PROPOSAL_FIELDS.area)}</ValidationMessage>}
      </Fieldset>
      <Fieldset>
        <Fieldset.Legend>{t('workEnvironmentMeasures.team.measureQuestion')}</Fieldset.Legend>
        <Field>
          <Label htmlFor={PROPOSAL_FIELDS.title}>{t('workEnvironmentMeasures.form.title')}</Label>
          <Field.Description id={`${PROPOSAL_FIELDS.title}-help`}>
            {t('workEnvironmentMeasures.form.titleHelp')}
          </Field.Description>
          <Input
            id={PROPOSAL_FIELDS.title}
            value={proposal.title}
            maxLength={MAX_TITLE_LENGTH}
            aria-describedby={`${PROPOSAL_FIELDS.title}-help ${PROPOSAL_FIELDS.title}-left`}
            aria-invalid={Boolean(errorFor(PROPOSAL_FIELDS.title)) || undefined}
            onChange={(event) => setProposal({ ...proposal, title: event.target.value })}
          />
          <Paragraph data-size="xs" id={`${PROPOSAL_FIELDS.title}-left`}>
            {t('workEnvironmentMeasures.form.charactersLeft', { count: charactersLeft(proposal.title) })}
          </Paragraph>
          {errorFor(PROPOSAL_FIELDS.title) && <ValidationMessage>{errorFor(PROPOSAL_FIELDS.title)}</ValidationMessage>}
        </Field>
        <TextField
          id={PROPOSAL_FIELDS.reasonLength}
          label={t('workEnvironmentMeasures.form.reason')}
          help={t('workEnvironmentMeasures.form.reasonHelp')}
          value={proposal.reason}
          error={errorFor(PROPOSAL_FIELDS.reasonLength)}
          onChange={(reason) => setProposal({ ...proposal, reason })}
        />
      </Fieldset>
      {propose.isError && !validationErrors(propose.error) && (
        <Alert data-color="danger">
          <Paragraph>{t('workEnvironmentMeasures.form.failed')}</Paragraph>
        </Alert>
      )}
      <div className={form.footer}>
        <Button type="submit" variant="primary" loading={propose.isPending}>
          {t('workEnvironmentMeasures.team.send')}
        </Button>
      </div>
    </form>
  );
};
